<template>
  <div>
    <page-header :title="title" :container-class="containerClass" />
    <div class="page-section">
      <div :class="containerClass">
        <div class="card mb-0">
          <div class="card-header">
            <div class="row align-items-center" style="white-space: nowrap">
              <div class="col-lg-auto">
                <form class="search-form search-form--light d-lg-inline-flex mb-8pt mb-lg-0" @submit.prevent="">
                  <b-form-input
                    class="w-lg-auto"
                    placeholder="Search name, email, phone"
                    v-model="searchTerm"
                    @input="onSearch"
                  />
                  <b-btn variant="flush" type="submit">
                    <md-icon v-text="'search'" />
                  </b-btn>
                </form>
              </div>
              <div class="col-lg-auto">
                <b-form-select
                  v-model="pathwayFilter"
                  :options="pathwayOptions"
                  size="sm"
                  @change="fetchLeads(1)"
                />
              </div>
              <div class="col-lg-auto">
                <b-form-select
                  v-model="statusFilter"
                  :options="statusOptions"
                  size="sm"
                  @change="fetchLeads(1)"
                />
              </div>
            </div>
          </div>

          <b-table
            :fields="tableColumns"
            :items="leads"
            :busy="isLoading"
            head-variant="light"
            class="table-nowrap"
            hover
            responsive
            no-local-sorting
            @row-clicked="openDetails"
          >
            <template #cell(name)="data">
              <strong>{{ leadName(data.item) }}</strong>
              <div class="text-muted small">{{ data.item.email }}</div>
            </template>

            <template #cell(phone)="data">
              {{ data.item.phone || '—' }}
            </template>

            <template #cell(pathway)="data">
              {{ get(data.item, 'pathway_title', '—') }}
            </template>

            <template #cell(school)="data">
              {{ get(data.item, 'school_name', '—') }}
            </template>

            <template #cell(universities)="data">
              <span v-if="(data.item.universities || []).length">
                {{ (data.item.universities || []).map(u => u.name).join(', ') }}
              </span>
              <span v-else class="text-muted">—</span>
            </template>

            <template #cell(zipcode)="data">
              {{ data.item.zipcode || '—' }}
            </template>

            <template #cell(readiness)="data">
              {{ data.item.readiness || '—' }}
            </template>

            <template #cell(status)="data">
              <b-badge :variant="statusVariant(data.item.status)">
                {{ data.item.status }}
              </b-badge>
            </template>

            <template #cell(created_at)="data">
              {{ formatDate(data.item.created_at) }}
            </template>

            <template #cell(actions)="data">
              <b-btn variant="link" size="sm" @click.stop="openDetails(data.item)">
                View
              </b-btn>
            </template>
          </b-table>

          <div class="card-footer">
            <pagination
              v-model="currentPage"
              :total-rows="totalLeads"
              :per-page="perPage"
              @change="onPageChange"
              aria-controls="leads-table"
            />
          </div>
        </div>
      </div>
    </div>

    <b-modal
      v-model="detailsOpen"
      :title="detailsLead ? leadName(detailsLead) : 'Lead'"
      hide-footer
      size="lg"
    >
      <div v-if="detailsLead" class="lead-details">
        <div class="row mb-3">
          <div class="col-sm-4 text-muted">Status</div>
          <div class="col-sm-8">
            <b-badge :variant="statusVariant(detailsLead.status)">{{ detailsLead.status }}</b-badge>
          </div>
        </div>
        <div class="row mb-2">
          <div class="col-sm-4 text-muted">Email</div>
          <div class="col-sm-8">{{ detailsLead.email || '—' }}</div>
        </div>
        <div class="row mb-2">
          <div class="col-sm-4 text-muted">Phone</div>
          <div class="col-sm-8">{{ detailsLead.phone || '—' }}</div>
        </div>
        <div class="row mb-2">
          <div class="col-sm-4 text-muted">ZIP</div>
          <div class="col-sm-8">{{ detailsLead.zipcode || '—' }}</div>
        </div>
        <div class="row mb-2">
          <div class="col-sm-4 text-muted">Readiness</div>
          <div class="col-sm-8">{{ detailsLead.readiness || '—' }}</div>
        </div>
        <div class="row mb-2">
          <div class="col-sm-4 text-muted">Marketing consent</div>
          <div class="col-sm-8">{{ detailsLead.consent_marketing ? 'Yes' : 'No' }}</div>
        </div>
        <hr />
        <div class="row mb-2">
          <div class="col-sm-4 text-muted">Pathway</div>
          <div class="col-sm-8">{{ get(detailsLead, 'pathway_title', '—') }}</div>
        </div>
        <div class="row mb-2">
          <div class="col-sm-4 text-muted">Feeder school</div>
          <div class="col-sm-8">{{ get(detailsLead, 'school_name', '—') }}</div>
        </div>
        <div class="row mb-2">
          <div class="col-sm-4 text-muted">Target universities</div>
          <div class="col-sm-8">
            <span v-if="(detailsLead.universities || []).length">
              {{ (detailsLead.universities || []).map(u => u.name).join(', ') }}
            </span>
            <span v-else>—</span>
          </div>
        </div>
        <hr />
        <div class="row mb-2">
          <div class="col-sm-4 text-muted">Source</div>
          <div class="col-sm-8">{{ detailsLead.source || '—' }}</div>
        </div>
        <div class="row mb-2">
          <div class="col-sm-4 text-muted">Created</div>
          <div class="col-sm-8">{{ formatDateTime(detailsLead.created_at) }}</div>
        </div>
        <div class="row mb-2" v-if="detailsLead.redirected_at">
          <div class="col-sm-4 text-muted">Email sent</div>
          <div class="col-sm-8">{{ formatDateTime(detailsLead.redirected_at) }}</div>
        </div>
        <div class="row mb-2" v-if="detailsLead.registered_at">
          <div class="col-sm-4 text-muted">Registered</div>
          <div class="col-sm-8">{{ formatDateTime(detailsLead.registered_at) }}</div>
        </div>
        <div class="row mb-2">
          <div class="col-sm-4 text-muted">Token</div>
          <div class="col-sm-8"><code class="small">{{ detailsLead.token }}</code></div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import PageHeader from '@/components/Ui/PageHeader.vue';
import { debounce, get } from 'lodash';
import { mapActions } from 'vuex';

import Page from '@/components/Page.vue';
import Pagination from '@/components/Ui/Pagination.vue';
import MdIcon from '@/components/Ui/MdIcon.vue';
import { DEFAULT_PAGE_SIZE } from '@/common/constants';

const STATUS_VARIANTS = {
  pending: 'secondary',
  redirected: 'info',
  registered: 'success',
  abandoned: 'warning',
};

export default {
  components: { MdIcon, PageHeader, Pagination },
  extends: Page,

  data() {
    return {
      title: 'Pathway Leads',
      isLoading: false,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalLeads: 0,
      searchTerm: '',
      pathwayFilter: null,
      statusFilter: null,
      leads: [],
      pathways: [],
      detailsOpen: false,
      detailsLead: null,
    };
  },

  computed: {
    tableColumns() {
      return [
        { key: 'name', label: 'Name' },
        { key: 'phone', label: 'Phone' },
        { key: 'pathway', label: 'Pathway' },
        { key: 'school', label: 'Feeder School' },
        { key: 'universities', label: 'Target Universities' },
        { key: 'zipcode', label: 'ZIP' },
        { key: 'readiness', label: 'Readiness' },
        { key: 'status', label: 'Status' },
        { key: 'created_at', label: 'Created' },
        { key: 'actions', label: '' },
      ];
    },
    pathwayOptions() {
      return [
        { value: null, text: 'All pathways' },
        ...this.pathways.map(p => ({ value: p.id, text: p.title })),
      ];
    },
    statusOptions() {
      return [
        { value: null, text: 'All statuses' },
        { value: 'pending', text: 'Pending' },
        { value: 'redirected', text: 'Email sent' },
        { value: 'registered', text: 'Registered' },
        { value: 'abandoned', text: 'Abandoned' },
      ];
    },
  },

  methods: {
    get,
    ...mapActions('pathways', ['getAllPathwayLeads', 'getAllPathways']),

    leadName(item) {
      const full = `${item.first_name || ''} ${item.last_name || ''}`.trim();
      return full || item.email || '—';
    },

    formatDate(value) {
      if (!value) return '—';
      try {
        return new Date(value).toLocaleDateString();
      } catch (_e) {
        return value;
      }
    },

    formatDateTime(value) {
      if (!value) return '—';
      try {
        return new Date(value).toLocaleString();
      } catch (_e) {
        return value;
      }
    },

    statusVariant(s) {
      return STATUS_VARIANTS[s] || 'secondary';
    },

    async fetchLeads(pageNum = 1) {
      this.isLoading = true;
      try {
        const response = await this.getAllPathwayLeads({
          limit: this.perPage,
          offset: (pageNum - 1) * this.perPage,
          ...(this.searchTerm && { search: this.searchTerm }),
          ...(this.pathwayFilter && { pathway: this.pathwayFilter }),
          ...(this.statusFilter && { status: this.statusFilter }),
        });
        this.leads = get(response, 'data.results', []);
        this.currentPage = pageNum;
        this.totalLeads = get(response, 'data.count', 0);
      } catch (_err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    async fetchPathways() {
      try {
        const response = await this.getAllPathways({ limit: 200 });
        this.pathways = get(response, 'data.results', []);
      } catch (_err) {
        // non-fatal: filter just won't be populated
      }
    },

    onPageChange(pageNum) {
      this.fetchLeads(pageNum);
    },

    onSearch() {
      this.debouncedSearch(this);
    },

    debouncedSearch: debounce(vm => {
      vm.fetchLeads();
    }, 500),

    openDetails(item) {
      this.detailsLead = item;
      this.detailsOpen = true;
    },
  },

  async mounted() {
    this.fetchPathways();
    this.fetchLeads();
  },
};
</script>
